import type { Ref } from 'vue';
import { DS_STACKED_OVERLAYS } from '~/composables/ds/useDsAnchoredOverlay';
import { DS_FOCUSABLE, dsRefocus } from '~/composables/ds/dsFocus';

// Piège de focus des modales DS (pas de dépendance `focus-trap` au projet) : tant que la modale est
// ouverte, Tab tourne dans son conteneur, et le focus revient au déclencheur à la fermeture.
//
// C'est la contrepartie d'`aria-modal="true"` : sans lui, on tabule dans l'écran RESTÉ derrière le
// voile alors que la boîte se déclare modale.
//
// L'écouteur vit sur `window` et non sur `document` : les overlais ancrés (useDsAnchoredOverlay)
// écoutent sur `document` et arrêtent la propagation, ce qui laisse un menu ouvert dans la modale
// se fermer seul sur Échap sans emporter la modale.

export interface DsFocusTrapOptions {
  // Échap : la modale décide de ce qu'elle en fait (annuler, fermer). Sans handler, Échap est ignoré.
  onEscape?: () => void;
  restoreFocus?: boolean;
  // A modal mounted by `v-if` unmounts while still open: the watch below never sees it close.
  restoreFocusOnUnmount?: boolean;
}

export function useDsFocusTrap (
  container: Ref<HTMLElement | null>,
  isOpen: Ref<boolean>,
  options: DsFocusTrapOptions = {},
): void {
  let lastFocused: HTMLElement | null = null;

  function focusables (): HTMLElement[] {
    return container.value ? Array.from(container.value.querySelectorAll<HTMLElement>(DS_FOCUSABLE)) : [];
  }

  // A dialog stacked above this one, such as a PrimeVue Dialog it opened, owns its own keys.
  function belongsToStackedModal (target: EventTarget | null): boolean {
    const modal = target instanceof Element ? target.closest('[role="dialog"], [role="alertdialog"]') : null;
    return Boolean(modal && container.value && !container.value.contains(modal) && !modal.contains(container.value));
  }

  function onKey (event: KeyboardEvent): void {
    if (belongsToStackedModal(event.target)) { return; }
    if (event.key === 'Escape') { options.onEscape?.(); return; }
    if (event.key !== 'Tab') { return; }
    const items = focusables();
    if (!items.length) { return; }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement as HTMLElement | null;
    // « Dedans » inclut les overlays que ce conteneur a OUVERTS : menu de select, calendrier,
    // confirmation imbriquée. Ils sont téléportés dans `body`, donc hors du sous-arbre, mais ils
    // appartiennent bien à la boîte du point de vue de l'utilisateur. Sans cette tolérance, Tab
    // rapatriait le focus dans le panneau dès qu'un menu s'ouvrait : le menu restait affiché et
    // devenait inatteignable au clavier, et la confirmation de suppression — qui n'installe pas de
    // piège à elle — ne se refermait plus qu'à Échap.
    const inside = (container.value?.contains(active) ?? false)
      || Boolean(active?.closest(DS_STACKED_OVERLAYS));
    // Boucle : Shift+Tab depuis le premier (ou hors modale) revient au dernier, et inversement.
    if (event.shiftKey && (active === first || !inside)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !inside)) {
      event.preventDefault();
      first.focus();
    }
  }

  watch(isOpen, (open) => {
    if (!import.meta.client) { return; }
    if (open) {
      // Le déclencheur, s'il en est un : `<body>` n'est pas focalisable, le retenir revient à ne
      // rien retenir. Ce cas arrive quand la boîte est ouverte autrement qu'au clic sur un contrôle
      // (route, action programmée) — mieux vaut alors ne pas toucher au focus à la fermeture que de
      // le poser sur le premier champ qui traîne.
      const active = document.activeElement as HTMLElement | null;
      lastFocused = active && active !== document.body ? active : null;
      window.addEventListener('keydown', onKey);
      // Focus initial sur le premier élément atteignable : défaut sûr, et il amorce la boucle.
      void nextTick(() => focusables()[0]?.focus());
    } else {
      window.removeEventListener('keydown', onKey);
      // Différé d'une frame et sous garde `isConnected` : à cet instant la boîte est encore montée
      // (transition de sortie), et le déclencheur a pu être démonté par le rendu qui suit — le
      // focus retomberait alors sur `<body>` et la tabulation reprendrait au début du document.
      if (options.restoreFocus !== false) { dsRefocus(lastFocused); }
      lastFocused = null;
    }
  }, { immediate: true });

  onUnmounted(() => {
    if (!import.meta.client) { return; }
    window.removeEventListener('keydown', onKey);
    if (isOpen.value && options.restoreFocusOnUnmount && options.restoreFocus !== false) { dsRefocus(lastFocused); }
  });
}
